import type { PaginationMetadata, PaginationQuery } from './common'
import type { ProductCategory } from './product'
import type { SalePaymentMethod } from './sale'

export const RIDER_SALE_STATUSES = [
  'draft',
  'done',
  'paid',
  'cancelled',
] as const

export type RiderSaleStatus = (typeof RIDER_SALE_STATUSES)[number]

export interface RiderSalePayment {
  method: SalePaymentMethod
  amount: number
}

export interface RiderSaleLineInput {
  productId: string
  dispatchedQty: number
  soldQty: number
  returnedQty?: number
}

export interface RiderSaleLine extends RiderSaleLineInput {
  productName: string
  category: ProductCategory
  remainingQty: number
  returnedQty?: number
  discrepancyQty?: number
  sellingPrice: number
  subtotal: number
  cogsPerUnit: number | null
}

export interface RiderSale {
  id: string
  workspaceId: string
  date: string
  riderId: string
  riderName: string
  /** Set once picked into a rider_payroll — null means available to pick. */
  payrollId: string | null
  /** Derived from payrollId — update/delete are rejected while set. */
  locked: boolean
  transactionCode: string | null
  lines: RiderSaleLine[]
  totalDispatchedQty: number
  totalSoldQty: number
  totalRemainingQty: number
  bonusBreakdown: Record<string, number>
  totalBonus: number
  salesIncomeBreakdown: Record<string, number>
  totalSalesIncome: number
  totalRevenue: number
  payments: RiderSalePayment[]
  totalCollected: number
  status: RiderSaleStatus
  notes: string | null
  cancelReason: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateRiderSaleReq {
  date: string
  riderId: string
  transactionCode?: string
  lines: RiderSaleLineInput[]
  payments?: RiderSalePayment[]
  status?: RiderSaleStatus
  notes?: string
}

export interface UpdateRiderSaleReq {
  date?: string
  riderId?: string
  transactionCode?: string
  lines?: RiderSaleLineInput[]
  payments?: RiderSalePayment[]
  status?: RiderSaleStatus
  notes?: string
}

export interface GetRiderSaleReq {
  q?: string
  riderId?: string
  status?: RiderSaleStatus
  payrollId?: string
  dateFrom?: string
  dateTo?: string
  pagination?: PaginationQuery
}

export interface RiderSaleList {
  items: RiderSale[]
  pagination: PaginationMetadata
}

/** Per-rider rollup of 'done' (worked, not yet paid) sales — 1 row per rider. */
export interface RiderPendingPaymentSummary {
  riderId: string
  riderName: string
  pendingCount: number
  oldestDate: string
  daysWaiting: number
  totalRevenue: number
  totalBonus: number
}

export interface RiderStockItem {
  productId: string
  productName: string
  sku?: string | null
  qty: number
}

export interface RiderStockSummary {
  riderId: string
  riderName: string
  items: RiderStockItem[]
  totalQty: number
}

